<template>
  <div class="c-fields-section flex flex-col gap-24">
    <!-- Opportunity Amount Field -->
    <div
      v-if="hasOrgSettingsPermission"
      class="flex justify-between items-center"
    >
      <div class="max-w-[480px]">
        <h3 class="font-bold mb-4">Opportunity Amount Field</h3>
        <span class="text-neutral-text text-sm mb-24">
          This is the field used to pull the amount associated with individual
          opportunities for
          <BittsLink
            url="https://help.crossbeam.com/en/articles/6797349-potential-revenue"
            text="Potential Revenue"
            open-in-new-tab
          />
          and
          <BittsLink
            url="https://help.crossbeam.com/en/collections/6037610-attribution"
            text="Attribution"
            open-in-new-tab
          />
        </span>
      </div>
      <BittsTooltip>
        <BittsSelect
          aria-label="Opportunity amount column"
          v-model="currentAmountColumn"
          :disabled="isAmountFieldSelectDisabled"
          :options="amountFieldList"
          class="current-amount-selector"
          placeholder="Opportunity Amount"
          dropdown-class-name="current-amount-selector-dropdown"
          @update:model-value="handleUpdateAmountColumn"
        >
          <template #suffix="{ option }">
            <BittsTag v-if="option.default" color="info" size="x-small">
              Default
            </BittsTag>
          </template>
        </BittsSelect>
        <template #title v-if="isAmountFieldSelectDisabled">
          Enable this CRM to map your opportunity and product fields
        </template>
      </BittsTooltip>
    </div>

    <!-- Opportunity Owner Field -->
    <FieldSettings
      v-if="hasOrgSettingsPermission"
      variant="opportunity-owner"
      :source-field-id="ownerSettings.sourceFieldId"
      :is-disabled="isDisabled"
      @field-changed="onUpdatedOpportunityOwner"
    />

    <!--Opportunity Type Field -->
    <NewAndExistingBusinessSettings
      v-if="hasScope('write:populations')"
      ref="settingsCmp"
      :is-disabled="isDisabled"
      @settings-changed="hasBusinessSettingsChanged = $event"
    />

    <template v-if="hasOrgSettingsPermission">
      <BittsDivider class="my-0" />

      <!-- Product Line Type Field -->
      <FieldSettings
        variant="product"
        :source-field-id="currentProductTypeSettings.sourceFieldId"
        :is-disabled="isDisabled"
        @field-changed="onUpdatedProduct"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  BittsDivider,
  BittsLink,
  BittsSelect,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { isEqual } from 'lodash';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import FieldSettings from '@/components/FieldSettings.vue';
import NewAndExistingBusinessSettings from '@/components/NewAndExistingBusinessSettings.vue';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import { usePreferredCRMFeed } from '@/composables/usePreferredCRMFeed';
import { MDM_PROPERTIES } from '@/constants/mdm';
import {
  useFeedsStore,
  usePotentialRevenueStore,
  useSourcesStore,
} from '@/stores';
import { ProductTypeSettings } from '@/stores/PotentialRevenueStore';
import {
  PotentialRevenueCustomAmount,
  PotentialRevenueOpportunityOwner,
  PotentialRevenueSettings,
} from '@/types/potential_revenue';

type Props = {
  isDisabled: boolean;
};
const { isDisabled = false } = defineProps<Props>();

const DOUBLE_PRECISION = 'double precision';
const MONEY = 'money';

type OwnerSettings = {
  sourceId: Nullable<number>;
  sourceFieldId: Nullable<number>;
};

const route = useRoute();
const { hasScope } = useAuth();
const { fieldMappingSettings, fieldMappingSaveCallback } =
  useDataSourceSettings();
const potRevSettingsStore = usePotentialRevenueStore();
const feedsStore = useFeedsStore();
const sourcesStore = useSourcesStore();
const { preferredCRMFeed } = usePreferredCRMFeed();

const preferredSourceId = ref<Nullable<number>>(null);
const settingsCmp = ref<InstanceType<typeof NewAndExistingBusinessSettings>>();
const potentialRevenueSettings = reactive<PotentialRevenueSettings>({});

const initialOwnerSettings: OwnerSettings = {
  sourceId: null,
  sourceFieldId: null,
};
const ownerSettings = reactive<OwnerSettings>({
  sourceId: null,
  sourceFieldId: null,
});

const initialProductTypeSettings = ref<ProductTypeSettings>({
  sourceId: null,
  sourceFieldId: null,
});
const currentProductTypeSettings = ref<ProductTypeSettings>({
  sourceId: null,
  sourceFieldId: null,
});

const initialAmountColumn = ref<number | null>(null);
const currentAmountColumn = ref<number | null>(null);

const hasBusinessSettingsChanged = ref<boolean>(false);

const haveSettingsChanged = computed(() => {
  const ownerChanged = !isEqual(initialOwnerSettings, ownerSettings);
  const amountChanged = !isEqual(
    initialAmountColumn.value,
    currentAmountColumn.value,
  );
  const productChanged = !isEqual(
    initialProductTypeSettings.value,
    currentProductTypeSettings.value,
  );
  const businessChanged = hasBusinessSettingsChanged.value;

  const hasValidBusinessSettings = settingsCmp.value?.isValidSettings;

  return (
    hasValidBusinessSettings &&
    (ownerChanged || amountChanged || productChanged || businessChanged)
  );
});

const isPreferredCRMFeed = computed(
  () => preferredCRMFeed.value?.id === Number(route.params.id),
);
const hasOrgSettingsPermission = computed(() => hasScope('write:organization'));

const amountFieldList = computed(() => {
  const feedId = preferredCRMFeed.value?.id;
  if (!feedId) return [];
  return sourcesStore.dealSources
    .filter((source) => source?.feed_id === feedId)
    .flatMap((source) => source.fields)
    .filter(
      (f) =>
        f.is_visible &&
        (f.pg_data_type === DOUBLE_PRECISION || f.pg_data_type === MONEY),
    )
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((f) => ({
      ...f,
      value: f.id,
      label: f.display_name,
      default: f.mdm_property === MDM_PROPERTIES.deal.AMOUNT,
    }));
});

const isAmountFieldSelectDisabled = computed(
  () => !hasDealSourcesAndFields.value || isDisabled,
);

const hasDealSources = computed(() => {
  return sourcesStore.dealSources.length > 0;
});
const hasDealSourcesAndFields = computed(() => {
  return hasDealSources.value && !!amountFieldList.value.length;
});

function setPreferredOwnerSetting() {
  if (!preferredSourceId.value) return;

  const defaultFieldId =
    sourcesStore.dealSources
      .find((source) => source.id === preferredSourceId.value)
      ?.fields.find(
        (item) => item.mdm_property === MDM_PROPERTIES.deal.OWNER_ID,
      )?.id ?? null;

  const preferredOwnerSetting =
    potentialRevenueSettings?.opportunity_owner_id_column;

  if (preferredOwnerSetting) {
    const ownerSourceId = Object.keys(preferredOwnerSetting)[0]
      ? Number(Object.keys(preferredOwnerSetting)[0])
      : null;
    const ownerSourceFieldId =
      preferredOwnerSetting[preferredSourceId.value] ?? defaultFieldId;

    Object.assign(initialOwnerSettings, {
      sourceId: ownerSourceId,
      sourceFieldId: ownerSourceFieldId,
    });
    Object.assign(ownerSettings, initialOwnerSettings);
  } else {
    Object.assign(initialOwnerSettings, {
      sourceId: preferredSourceId.value,
      sourceFieldId: defaultFieldId,
    });
    Object.assign(ownerSettings, initialOwnerSettings);
  }
}

function onUpdatedOpportunityOwner({
  sourceFieldId,
  sourceId,
}: {
  sourceFieldId: number;
  sourceId?: number;
}) {
  ownerSettings.sourceFieldId = sourceFieldId;
  if (sourceId) {
    ownerSettings.sourceId = sourceId;
  }
  if (!ownerSettings.sourceFieldId || !ownerSettings.sourceId) return;
  potentialRevenueSettings.opportunity_owner_id_column = {};
  potentialRevenueSettings.opportunity_owner_id_column = {
    [ownerSettings.sourceId]: ownerSettings.sourceFieldId,
  } as PotentialRevenueOpportunityOwner;
  fieldMappingSettings.value.potentialRevenueSettings =
    potentialRevenueSettings;
}

function onUpdatedProduct({
  sourceFieldId,
  sourceId,
}: {
  sourceFieldId: number;
  sourceId?: number;
}) {
  if (!sourceId) return;
  currentProductTypeSettings.value = { sourceId, sourceFieldId };
  potRevSettingsStore.setProductTypeSettings(sourceId, sourceFieldId);
}

function setupAmountColumn() {
  if (
    potentialRevenueSettings.amount_column_potential_influence &&
    preferredSourceId.value &&
    potentialRevenueSettings.amount_column_potential_influence[
      preferredSourceId.value
    ]
  ) {
    const preferredPotRevAmount =
      potentialRevenueSettings.amount_column_potential_influence[
        preferredSourceId.value
      ];
    initialAmountColumn.value =
      amountFieldList.value.find((item) => {
        return item.id === preferredPotRevAmount;
      })?.value || null;
    currentAmountColumn.value = initialAmountColumn.value;
  } else {
    const defaultField = amountFieldList.value.find(
      (item) => item.mdm_property === MDM_PROPERTIES.deal.AMOUNT,
    );
    if (defaultField && defaultField.id && defaultField.source_id) {
      if (!potentialRevenueSettings.amount_column_potential_influence)
        potentialRevenueSettings.amount_column_potential_influence = {};
      potentialRevenueSettings.amount_column_potential_influence = {
        [defaultField.source_id]: defaultField.id,
      } as PotentialRevenueCustomAmount;
      initialAmountColumn.value = defaultField.id;
      currentAmountColumn.value = defaultField.id;
    }
  }
}

function handleUpdateAmountColumn(fieldId: number) {
  const selectedField = sourcesStore.getSourceFieldById(fieldId);
  if (selectedField?.id && selectedField?.source_id) {
    if (currentAmountColumn.value !== selectedField.id)
      currentAmountColumn.value = selectedField.id;
    potentialRevenueSettings.amount_column_potential_influence = {
      [selectedField.source_id]: selectedField.id,
    } as PotentialRevenueCustomAmount;
    fieldMappingSettings.value.potentialRevenueSettings =
      potentialRevenueSettings;
  }
}

// Update fieldMappingSettings when component mounts or settings change
watch(
  () => settingsCmp.value,
  (newVal) => {
    if (newVal) {
      fieldMappingSettings.value.settingsCmp = newVal;
    }
  },
  { immediate: true },
);

watch(
  () => potentialRevenueSettings,
  (newVal) => {
    fieldMappingSettings.value.potentialRevenueSettings = newVal;
  },
  { deep: true },
);

watch(hasBusinessSettingsChanged, (newVal) => {
  fieldMappingSettings.value.hasBusinessSettingsChanged = newVal;
});

watch(
  [currentProductTypeSettings, initialProductTypeSettings],
  ([current, initial]) => {
    fieldMappingSettings.value.hasProductTypeChanged = !isEqual(
      current,
      initial,
    );
  },
  { deep: true },
);

function setPreferredSourceId() {
  preferredSourceId.value =
    sourcesStore.dealSources.find(
      (source) => source.feed_id === preferredCRMFeed.value?.id,
    )?.id || null;
}

function setPreferredProductTypeSetting() {
  initialProductTypeSettings.value = {
    ...potRevSettingsStore.productTypeSettings,
  };
  currentProductTypeSettings.value = {
    ...potRevSettingsStore.productTypeSettings,
  };
}

// Function to reset all initial states after save
function resetInitialStatesAfterSave() {
  Object.assign(
    potentialRevenueSettings,
    potRevSettingsStore.potentialRevenueSettings,
  );
  setupAmountColumn();
  setPreferredOwnerSetting();
  setPreferredProductTypeSetting();
  hasBusinessSettingsChanged.value = false;
}

onMounted(async () => {
  if (!isPreferredCRMFeed.value) return;
  await Promise.all([
    potRevSettingsStore.readySync,
    feedsStore.readySync,
    sourcesStore.readySync,
  ]);

  Object.assign(
    potentialRevenueSettings,
    potRevSettingsStore.potentialRevenueSettings,
  );

  setPreferredSourceId();
  setupAmountColumn();
  setPreferredOwnerSetting();
  setPreferredProductTypeSetting();

  // Register callback for post-save reset
  fieldMappingSaveCallback.value = resetInitialStatesAfterSave;
});

watch(preferredCRMFeed, () => {
  if (!isPreferredCRMFeed.value) return;
  setPreferredSourceId();
  setupAmountColumn();
  setPreferredOwnerSetting();
  setPreferredProductTypeSetting();
});

watch(
  () => isDisabled,
  (disabled) => {
    if (disabled || !isPreferredCRMFeed.value) {
      currentAmountColumn.value = null;
    } else if (isPreferredCRMFeed.value) {
      // When toggling OFF > ON, then canceling the modal, revert to initial values
      currentAmountColumn.value = initialAmountColumn.value;
      ownerSettings.sourceFieldId = initialOwnerSettings.sourceFieldId;
      currentProductTypeSettings.value.sourceFieldId =
        initialProductTypeSettings.value.sourceFieldId;
    } else {
      setupAmountColumn();
    }
  },
);

defineExpose({
  haveSettingsChanged,
});
</script>

<style scoped lang="pcss">
@reference "../../../../../css/theme.css";

.current-amount-selector {
  @apply w-[240px] mb-8 mt-24;
  .bitts-tag .ant-tag {
    @apply mr-0;
  }
  .ant-select .ant-select-selector {
    .ant-select-selection-item div {
      @apply w-full;
      div {
        @apply m-auto flex-1;
      }
    }
  }
}
.current-amount-selector-dropdown {
  .ant-select-item-option-selected {
    @apply gap-8;
    .bitts-tag .ant-tag {
      @apply mr-0;
    }
  }
}
</style>
